import { Accessor, LogicX, ZMath, ZValidate } from "../../../../STATIC";
import { XInput } from "../../../../_X";
import _ from "lodash";
// eslint-disable-next-line no-unused-vars
import React, { CSSProperties, useEffect, useState } from "react";

/**
 * @typedef {import("IZOX/_X/XInput").xinputparams} xinputparams
 * @typedef {import("IZOX/_X/XInput").xinputstyleparams} xinputstyleparams
 * @typedef {import("IZOX/STATIC/ZValidate").fvalidate} fvalidate
 * 
 * @typedef {Object} qfnumber
 * @property {String} name
 * @property {any} defaultValue
 * @property {Function} _onChange
 * @property {Function} _onSetDefault
 * @property {any} formValue
 * @property {({doc, field, addOns}) => Boolean} isError
 * @property {fvalidate | fvalidate[]} validators
 * @property {*} addOns
 * @property {Boolean} integerOnly
 * 
 * @typedef {qfnumber & xinputparams & xinputstyleparams} qfnumberparams
 * 
 * @param {qfnumberparams} param0
 * @returns 
 */
const QFNumber = ({
  name = undefined,
  label = undefined,
  _onChange = null,
  _onSetDefault = null,
  defaultValue = undefined,
  formValue = {},
  addOns = undefined,
  updateVia = undefined, 
  updateField = undefined,
  forceUpdateField = undefined,
  performanceBoost = undefined,
  type = "text",
  isError = undefined,
  validators = [],
  integerOnly = false,
  ...others
}) => {

  const [value, setValue] = useState(defaultValue);
  const [display, setDisplay] = useState("");
  const [valid, setValid] = useState(false);

  const regex = integerOnly ? /[^0-9-]/g : /[^0-9.-]/g;

  useEffect(() => {
    if(defaultValue) {
      _onSetDefault?.(name, defaultValue);
      setValue(defaultValue);
      if(_.isNaN(defaultValue)){
        setDisplay(defaultValue);
        setValid(false);
      }else{
        setDisplay(String(defaultValue));
        setValid(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  useEffect(() => {
    let _value = Accessor.Get(formValue, name);
    setValue(_value);
    if(_.isNaN(_value)){
      setDisplay(_value);
      setValid(false);
    }else{
      setDisplay(String(_value ?? ""));
      setValid(true);
    }
  }, [formValue, name]);

  if(_.isUndefined(name)) return null;

  let { readOnly, min, max } = others;

  let _error = !valid;
  if(isError){
    _error = _error || isError({doc: formValue, field: value, addOns});
  }
  if(!_.isEmpty(validators)){
    _error = _error || !ZValidate.Check(value, validators);
  }

  const onChange = (value) => {
    value = value.replace(regex, "");
    if(readOnly) return;
    if(_.split(value, ".").length > 2){
      value = value.substring(0, _.lastIndexOf(value, "."));
    }
    if (value === "" || value === "-" || value?.[value.length - 1] === ".") {
      setDisplay(_.isUndefined(value) ? "" : String(value));
      setValid(false);
    } else {
      let ivalue = ZMath.Cap(value, min ?? -Infinity, max ?? Infinity);
      _onChange?.(name, Number(ivalue));
    }
  };

  return (
    <XInput
      keyboardType="number-pad"
      value={display}
      isControlled={true}
      onChangeText={onChange}
      type={"number"}
      pL={12}
      readOnly={readOnly}
      {...others}
      error={_error}
      />
  );
};

export default React.memo(QFNumber, (prevProps, nextProps) => {
  let propIdentical = Accessor.IsIdentical(nextProps, prevProps);
  if(propIdentical){ return true; }
  
  let {updateVia, updateField, forceUpdateField, performanceBoost, name} = nextProps ?? {};
   
  if(updateVia !== "update") return false;

  if(performanceBoost){
    if(name.includes(updateField)){
      return false;
    }

    if(forceUpdateField?.length > 0 && LogicX.includesOneOf(name, forceUpdateField)){
      return false;
    }

    return true;
  }
  return false;
});

